import React, { useEffect, useRef, useState } from "react";
import api, { assetSrc } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DashboardShell from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Sparkles, Palette, ExternalLink, Share2, Copy, Check, User } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import BillingCard from "@/components/BillingCard";
import AvailabilityCard from "@/components/AvailabilityCard";

const PRESETS = [
  { name: "Champagne (default)", color: "#C9A24A" },
  { name: "Emerald", color: "#0F8F6E" },
  { name: "Crimson", color: "#B23A48" },
  { name: "Cobalt", color: "#2456C2" },
  { name: "Saffron", color: "#E08A1E" },
  { name: "Magenta", color: "#A23E97" },
];

export default function Branding() {
  const { user, setUser } = useAuth();
  const fileRef = useRef();
  const photoRef = useRef();
  const [accent, setAccent] = useState(user?.accent_color || "#C9A24A");
  const [agency, setAgency] = useState(user?.agency_name || "");
  const [logoPreview, setLogoPreview] = useState(assetSrc(user?.logo_url) || null);
  const [logoBase64, setLogoBase64] = useState(null);
  const [logoMime, setLogoMime] = useState("image/png");
  const [photoPreview, setPhotoPreview] = useState(assetSrc(user?.picture) || null);
  const [photoBase64, setPhotoBase64] = useState(null);
  const [photoMime, setPhotoMime] = useState("image/jpeg");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAccent(user?.accent_color || "#C9A24A");
    setAgency(user?.agency_name || "");
    setLogoPreview(assetSrc(user?.logo_url) || null);
    setPhotoPreview(assetSrc(user?.picture) || null);
  }, [user?.accent_color, user?.agency_name, user?.logo_url, user?.picture]);

  const onPickLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/(png|jpe?g|webp)/i.test(f.type)) {
      toast.error("Use PNG / JPEG / WEBP only.");
      return;
    }
    if (f.size > 2_000_000) {
      toast.error("Logo must be under 2 MB.");
      return;
    }
    const b64 = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result.split(",")[1]);
      r.onerror = rej;
      r.readAsDataURL(f);
    });
    setLogoBase64(b64);
    setLogoMime(f.type);
    setLogoPreview(URL.createObjectURL(f));
  };

  const onPickPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/(png|jpe?g|webp)/i.test(f.type)) {
      toast.error("Use PNG / JPEG / WEBP only.");
      return;
    }
    if (f.size > 3_000_000) {
      toast.error("Headshot must be under 3 MB.");
      return;
    }
    const b64 = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result.split(",")[1]);
      r.onerror = rej;
      r.readAsDataURL(f);
    });
    setPhotoBase64(b64);
    setPhotoMime(f.type);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { accent_color: accent, agency_name: agency };
      if (logoBase64) {
        payload.logo_base64 = logoBase64;
        payload.logo_mime = logoMime;
      }
      if (photoBase64) {
        payload.photo_base64 = photoBase64;
        payload.photo_mime = photoMime;
      }
      const { data } = await api.post("/auth/branding", payload);
      setUser(data);
      setLogoBase64(null);
      setPhotoBase64(null);
      toast.success("Branding saved. Your own branded portal now uses these colours.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save branding.");
    } finally {
      setSaving(false);
    }
  };

  const clearPhoto = async () => {
    if (!window.confirm("Remove your headshot? Your portal will fall back to the default avatar.")) return;
    setSaving(true);
    try {
      const { data } = await api.post("/auth/branding", { clear_photo: true });
      setUser(data);
      setPhotoPreview(null);
      setPhotoBase64(null);
      if (photoRef.current) photoRef.current.value = "";
      toast.success("Headshot removed.");
    } catch {
      toast.error("Could not remove headshot.");
    } finally {
      setSaving(false);
    }
  };

  const clearAccent = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/auth/branding", { accent_color: "" });
      setUser(data);
      setAccent("#C9A24A");
      toast.success("Accent reset to default.");
    } catch {
      toast.error("Reset failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardShell>
      <div className="overline text-sg-gold-deep">PRO PLAN · VANITY BRANDING</div>
      <h1 className="font-display text-4xl lg:text-5xl text-sg-navy mt-2">
        Make your own branded portal <span className="font-serif-italic text-sg-gold-deep">unmistakably yours.</span>
      </h1>
      <p className="text-sg-mute mt-2 max-w-2xl">
        Override the default champagne accent and add your agency logo. Changes apply instantly to every page of your agent portal at <span className="font-mono text-sg-navy">/agent/{user?.slug}</span>.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        {/* Accent + Agency */}
        <div className="bg-white border border-sg-line rounded-3xl p-7 space-y-6" data-testid="branding-card-accent">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-sg-cream-soft border border-sg-line flex items-center justify-center text-sg-gold-deep"><Palette className="w-4 h-4" /></div>
            <h3 className="font-display text-2xl text-sg-navy">Accent colour</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.color}
                onClick={() => setAccent(p.color)}
                className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${
                  accent === p.color ? "border-sg-navy bg-sg-cream-soft" : "border-sg-line"
                }`}
                data-testid={`accent-preset-${p.color.toLowerCase().replace("#", "")}`}
              >
                <span className="w-4 h-4 rounded-full" style={{ background: p.color }} />
                {p.name}
              </button>
            ))}
          </div>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label className="overline text-sg-gold-deep">Custom hex</Label>
              <Input
                value={accent}
                onChange={(e) => setAccent(e.target.value.toUpperCase())}
                placeholder="#C9A24A"
                className="font-mono rounded-full border-sg-line"
                data-testid="accent-hex-input"
              />
            </div>
            <input
              type="color"
              value={accent}
              onChange={(e) => setAccent(e.target.value.toUpperCase())}
              className="w-12 h-12 rounded-full border border-sg-line cursor-pointer"
              data-testid="accent-color-picker"
            />
          </div>
          <div className="space-y-2">
            <Label className="overline text-sg-gold-deep">Agency name (optional)</Label>
            <Input
              value={agency}
              onChange={(e) => setAgency(e.target.value)}
              placeholder="e.g., Mehta Realty Partners"
              className="rounded-full border-sg-line"
              data-testid="agency-name-input"
            />
          </div>
          <Button variant="outline" onClick={clearAccent} disabled={saving} className="rounded-full border-sg-line" data-testid="reset-accent">
            Reset accent to default
          </Button>
        </div>

        {/* Logo + Headshot — stacked in the right column */}
        <div className="space-y-6">
          <div className="bg-white border border-sg-line rounded-3xl p-7 space-y-6" data-testid="branding-card-logo">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-sg-cream-soft border border-sg-line flex items-center justify-center text-sg-gold-deep"><Sparkles className="w-4 h-4" /></div>
            <h3 className="font-display text-2xl text-sg-navy">Agency logo</h3>
          </div>
          <div className="rounded-2xl border border-dashed border-sg-line p-6 text-center bg-sg-cream-soft/60">
            {logoPreview ? (
              <img src={logoPreview} alt="Logo" className="mx-auto max-h-24 object-contain" data-testid="logo-preview" />
            ) : (
              <p className="text-sm text-sg-mute">No logo yet — upload a PNG / JPEG / WEBP up to 2 MB.</p>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={onPickLogo}
              className="hidden"
              data-testid="logo-file-input"
            />
            <Button
              onClick={() => fileRef.current?.click()}
              className="mt-4 rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white"
              data-testid="logo-choose-btn"
            >
              <Upload className="w-4 h-4 mr-1" /> {logoPreview ? "Replace logo" : "Choose image"}
            </Button>
          </div>
          <p className="text-xs text-sg-mute">
            For the best result, use a transparent PNG (square or wide). Your logo replaces the avatar in your own branded portal&apos;s nav.
          </p>
        </div>

        {/* Headshot */}
        <div className="bg-white border border-sg-line rounded-3xl p-7 space-y-6" data-testid="branding-card-photo">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-sg-cream-soft border border-sg-line flex items-center justify-center text-sg-gold-deep"><User className="w-4 h-4" /></div>
            <div className="flex-1">
              <h3 className="font-display text-2xl text-sg-navy">Your headshot</h3>
              <p className="text-xs text-sg-mute mt-0.5">Replaces the default avatar on your portal hero and Landing-page card.</p>
            </div>
          </div>
          <div className="rounded-2xl border border-dashed border-sg-line p-6 text-center bg-sg-cream-soft/60">
            {photoPreview ? (
              <img
                src={photoPreview}
                alt="Your headshot"
                className="mx-auto w-24 h-24 rounded-full object-cover border border-sg-line"
                data-testid="photo-preview"
              />
            ) : (
              <div className="flex flex-col items-center gap-2">
                <div className="w-24 h-24 rounded-full bg-white border border-sg-line flex items-center justify-center">
                  <User className="w-10 h-10 text-sg-mute/40" />
                </div>
                <p className="text-sm text-sg-mute">No headshot yet — upload PNG / JPEG / WEBP up to 3 MB.</p>
              </div>
            )}
            <input
              ref={photoRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={onPickPhoto}
              className="hidden"
              data-testid="photo-file-input"
            />
            <div className="mt-4 flex items-center justify-center gap-2">
              <Button
                onClick={() => photoRef.current?.click()}
                className="rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white"
                data-testid="photo-choose-btn"
              >
                <Upload className="w-4 h-4 mr-1" /> {photoPreview ? "Replace headshot" : "Choose image"}
              </Button>
              {photoPreview && (
                <Button
                  variant="outline"
                  onClick={clearPhoto}
                  disabled={saving}
                  className="rounded-full border-sg-line"
                  data-testid="photo-remove-btn"
                >
                  Remove
                </Button>
              )}
            </div>
          </div>
          <p className="text-xs text-sg-mute">
            Square shots crop cleanest. Aim for a well-lit, professional photo — buyers convert 25-35 % better with a real face on the card.
          </p>
        </div>
        </div>
      </div>

      {/* Live preview band */}
      <div
        className="mt-8 rounded-3xl p-6 text-white relative overflow-hidden"
        style={{ background: "#0B1B33" }}
        data-testid="branding-live-preview"
      >
        <div className="overline mb-2" style={{ color: accent }}>LIVE PREVIEW</div>
        <h3 className="font-display text-3xl">How your portal looks now.</h3>
        <div className="mt-5 flex flex-wrap gap-3 items-center">
          <span
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-semibold"
            style={{ background: accent, color: "#0B1B33" }}
            data-testid="preview-wa-pill"
          >
            WhatsApp {user?.name?.split(" ")[0]}
          </span>
          <span
            className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs uppercase tracking-[0.18em]"
            style={{ background: accent, color: "#0B1B33" }}
          >
            MahaCRITI Reg No: {user?.maharera_number}
          </span>
          {agency && (
            <span className="text-sm text-white/75 italic">— {agency}</span>
          )}
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-3 items-center">
        <Button onClick={save} disabled={saving} className="rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white h-12 px-6" data-testid="branding-save">
          {saving ? "Saving…" : "Save branding"}
        </Button>
        <Link to={`/agent/${user?.slug}`} target="_blank" className="pill-outline inline-flex items-center gap-2" data-testid="branding-open-microsite">
          Open my branded portal <ExternalLink className="w-4 h-4" />
        </Link>
      </div>

      <ShareMyPage slug={user?.slug} name={user?.name} />

      <div className="mt-8">
        <AvailabilityCard />
      </div>

      <div className="mt-8">
        <BillingCard />
      </div>
    </DashboardShell>
  );
}

function ShareMyPage({ slug, name }) {
  const [copied, setCopied] = React.useState(null);
  if (!slug) return null;

  const origin = (typeof window !== "undefined" && window.location?.origin) || "https://inpropportal.com";
  const microsite = `${origin}/agent/${slug}`;
  const apiBase = process.env.REACT_APP_BACKEND_URL || origin;
  const shareUrl = `${apiBase}/api/og/${slug}`;
  const firstName = (name || "").split(" ")[0] || "Agent";

  const waText = `Hi! I'm ${name || "your INPropPortal agent"}. Here's my Pune real-estate portal — verified, MahaCRITI-aligned, and updated daily: ${shareUrl}`;
  const liText = `Discover my Pune real-estate branded portal — INPropPortal verified. ${shareUrl}`;
  const xText  = `My Pune real-estate page on @INPropPortal · ${shareUrl}`;

  const copy = async (label, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      toast.error("Could not copy. Long-press to copy manually.");
    }
  };

  const tiles = [
    { id: "share-link",   label: "Share link (rich preview)", value: shareUrl, hint: "Use this in WhatsApp / LinkedIn / iMessage — they will render a rich preview card." },
    { id: "direct-link",  label: "Direct branded portal URL",     value: microsite, hint: "Plain link — opens straight into your page (no preview card)." },
    { id: "wa-blurb",     label: "WhatsApp blurb",            value: waText,    hint: "Drop into a status, group, or DM." },
    { id: "li-blurb",     label: "LinkedIn caption",          value: liText,    hint: "Tighter, professional tone." },
    { id: "x-blurb",      label: "X / Twitter post",          value: xText,     hint: "Short-form." },
  ];

  return (
    <div className="mt-12 bg-white border border-sg-line rounded-3xl p-7" data-testid="share-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-sg-cream-soft border border-sg-line flex items-center justify-center text-sg-gold-deep">
          <Share2 className="w-4 h-4" />
        </div>
        <div>
          <div className="overline text-sg-gold-deep">DISTRIBUTION</div>
          <h3 className="font-display text-2xl text-sg-navy leading-tight">Share <span className="font-serif-italic text-sg-gold-deep">{firstName}&apos;s</span> page.</h3>
        </div>
      </div>
      <p className="text-sm text-sg-mute max-w-2xl mb-5">
        The first link uses a server-rendered preview endpoint so WhatsApp, LinkedIn, and iMessage display your <strong>name, MahaCRITI #, locality and logo</strong> as a rich card. Humans get redirected to your branded portal instantly.
      </p>

      <div className="space-y-3">
        {tiles.map((t) => (
          <div key={t.id} className="border border-sg-line rounded-2xl p-4 bg-sg-cream-soft/40" data-testid={`share-tile-${t.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div>
                <div className="text-sm font-semibold text-sg-navy">{t.label}</div>
                <div className="text-xs text-sg-mute">{t.hint}</div>
              </div>
              <Button
                size="sm"
                onClick={() => copy(t.label, t.value)}
                variant="outline"
                className="rounded-full border-sg-line text-sg-navy"
                data-testid={`share-copy-${t.id}`}
              >
                {copied === t.label ? <><Check className="w-3.5 h-3.5 mr-1" /> Copied</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Copy</>}
              </Button>
            </div>
            <div className="font-mono text-xs text-sg-mute break-all bg-white border border-dashed border-sg-line rounded-xl px-3 py-2">{t.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
